import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FundStatus, TrancheVisibility } from 'further-types/fund';
import { CalendarDay, validationRegexes } from 'further-ui/utils';
import { isMongoId } from 'further-ui/validations/helpers';

const fileSchema = z.object({
  fileName: z.string(),
  filePath: z.string(),
  previewUri: z.string().optional(),
});

export const trancheFormSchema = z.object({
  firmId: z.string().refine(isMongoId(), 'Please select firm.'),
  fundName: z.string().min(1, 'Please enter tranche name.'),
  fundStatus: z.nativeEnum(FundStatus),
  startDate: z.instanceof(CalendarDay).nullable(),
  finishDate: z
    .instanceof(CalendarDay)
    .nullable()
    .refine((val): val is CalendarDay => val !== null, {
      message: 'Please select finish date.',
    }),
  closedDate: z.instanceof(CalendarDay).nullable(),
  tag: z.string().min(1, 'Please select fund type.'),
  isProfessionalClientsOnly: z.boolean(),
  trancheVisibility: z.nativeEnum(TrancheVisibility),
  fundRaiseLimit: z.number().nullable(),
  incomeTaxRebate: z.string(),
  targetReturn: z.string(),
  targetPortfolioSize: z.string(),
  minimumDirectInvestment: z.string(),
  minimumAdvisedInvestment: z.string(),
  minimumIncludesAdviceFee: z.boolean(),
  applicationFormFile: fileSchema.nullable(),
  prospectusFile: fileSchema.nullable(),
  keyInfoFile: fileSchema.nullable(),
  additionalDocuments: z.array(fileSchema),
  defaultCoreFee: z.string(),
  defaultPerformanceFee: z.string(),
  firmBankDetailId: z.string(),
  title: z.string(),
  customBankTransferReference: z.string(),
  whatToExpectNow: z.string(),
  additionalAttachment: fileSchema.nullable(),
  additionalInfo: z
    .string()
    .transform((value) =>
      value
        .split(';')
        .map((email) => email.trim())
        .join(';'),
    )
    .refine(
      (value) =>
        !value ||
        value
          .split(';')
          .every((email) => !email || validationRegexes.email.test(email)),
      {
        message:
          'Separate emails with a semi-colon, e.g. name@domain.com;name2@domain.com',
      },
    ),
});

export const tranchePublishableSchema = trancheFormSchema
  .extend({
    firmBankDetailId: z.string().min(1, 'Please select a payment account.'),
    isSyndicate: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (!data.isSyndicate && !data.applicationFormFile) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please upload a legal agreement.',
        path: ['applicationFormFile'],
      });
    }
  });

export type TrancheFormInput = z.input<typeof trancheFormSchema>;
export type TrancheFormValues = z.output<typeof trancheFormSchema>;

type UseTrancheFormParams = {
  firmId?: string;
  values?: TrancheFormInput;
};

const useTrancheForm = ({ firmId, values }: UseTrancheFormParams) =>
  useForm<TrancheFormInput, unknown, TrancheFormValues>({
    resolver: zodResolver(trancheFormSchema),
    defaultValues: {
      firmId: firmId ?? '',
      fundName: '',
      fundStatus: FundStatus.Draft,
      startDate: null,
      finishDate: null,
      closedDate: null,
      tag: '',
      isProfessionalClientsOnly: false,
      trancheVisibility: TrancheVisibility.ShowOnFirm,
      fundRaiseLimit: null,
      incomeTaxRebate: '',
      targetReturn: '',
      targetPortfolioSize: '',
      minimumDirectInvestment: '',
      minimumAdvisedInvestment: '',
      minimumIncludesAdviceFee: false,
      applicationFormFile: null,
      prospectusFile: null,
      keyInfoFile: null,
      additionalDocuments: [],
      defaultCoreFee: '',
      defaultPerformanceFee: '',
      firmBankDetailId: '',
      title: '',
      customBankTransferReference: '',
      whatToExpectNow: '',
      additionalAttachment: null,
      additionalInfo: '',
    },
    values,
  });

export type UseTrancheFormReturn = ReturnType<typeof useTrancheForm>;

export default useTrancheForm;
